/**
 * For using custom Y emoji
 * https://www.w3schools.com/charsets/ref_emoji.asp
 * https://medium.com/@seanmcp/%EF%B8%8F-how-to-use-emojis-in-react-d23bbf608bf7
 * https://medium.com/making-faces-and-other-emoji/emoji-on-the-web-537c5769dffa
 *
 * Usage:
 * <EmojiIcon name="productService" animated />
 *
 * <EmojiIcon name="calendar" animatedSlow />
 *
 * <button className="group">
 *   <EmojiIcon name="design" animatedHover />
 * </button>
 */

import React from "react";
import PropTypes from "prop-types";
import Emoji from "a11y-react-emoji/lib/Emoji";
import classNames from "classnames";

const EmojiIcon = ({
  className,
  name,
  animated,
  animatedSlow,
  animatedHover,
}) => {
  const icons = {
    businessDev: {
      // 📈
      symbol: <>&#128200;</>,
      label: "Business development",
    },
    calendar: {
      // 📅 Calendar
      symbol: <>&#128197;</>,
      label: "Calendar",
    },
    clap: {
      // 👏
      symbol: <>&#128079;</>,
      label: "Clapping hands sign",
    },
    coffee: {
      symbol: <>&#9749;</>,
      label: "Cup of coffee",
    },
    content: {
      // 📁
      symbol: <>&#128193;</>,
      label: "Content communication",
    },
    design: {
      // 📐
      symbol: <>&#128208;</>,
      label: "Design",
    },
    food: {
      // 🍴 Fork and Knife
      symbol: <>&#127860;</>,
      label: "Food",
    },
    flagUk: {
      // 🇬🇧 this emoji doesn't work, had to define custom glyph
      // symbol: <>🇬🇧</>,
      // 
      symbol: <>&#57378;</>,
      label: "UK Flag",
    },
    flagNo: {
      // 🇳🇴 this emoji doesn't work, had to define custom glyph
      // symbol: <>🇳🇴</>,
      // 
      symbol: <>&#57379;</>,
      label: "NO Flag",
    },
    laptop: {
      // 💻
      symbol: <>&#128187;</>,
      label: "Laptop",
    },
    important: {
      // ⚠
      symbol: <>&#9888;</>,
      label: "Important",
    },
    language: {
      // 👄 Mouth
      symbol: <>&#128068;</>,
      label: "language",
    },
    leadership: {
      // 👑
      symbol: <>&#128081;</>,
      label: "Leadership",
    },
    lightbulb: {
      // 💡 Electric light bulb
      symbol: <>&#128161;</>,
      label: "Lightbulb",
    },
    livestream: {
      // 📡
      symbol: <>&#128225;</>,
      label: "Livestream",
    },
    magnifyingGlass: {
      // 🔍 LEFT-POINTING MAGNIFYING GLASS
      symbol: <>&#128269;</>,
      label: "Magnifying glass",
    },
    party: {
      // 🎉
      symbol: <>&#127881;</>,
      label: "Party",
    },
    pen: {
      // 🖊 LOWER LEFT BALLPOINT PEN
      symbol: <>&#128394;</>,
      label: "Pen",
    },
    postIt: {
      // 🗒 Spiral notepad
      symbol: <>&#128466;</>,
      label: "Post-it",
    },
    presentation: {
      // 📢
      symbol: <>&#128226;</>,
      label: "Presentation",
    },
    productService: {
      // 🔄
      symbol: <>&#128260;</>,
      label: "Product & Service",
    },
    question: {
      // ❓ BLACK QUESTION MARK ORNAMENT
      symbol: <>&#10067;</>,
      label: "Question",
    },
    smile: {
      // 😊
      symbol: <>&#128522;</>,
      label: "Smiling face with smiling eyes",
    },
    speakers: {
      // 📣
      symbol: <>&#128227;</>,
      label: "Speakers",
    },
    stage: {
      // 🎤
      symbol: <>&#127908;</>,
      label: "Stage",
    },
    sustainability: {
      // 🌎
      symbol: <>&#127758;</>,
      label: "Sustainability",
    },
    tech: {
      // 🖥 Desktop computer
      symbol: <>&#128421;</>,
      label: "Tech",
    },
    tent: {
      // 🎪 Circus tent
      symbol: <>&#127914;</>,
      label: "tent",
    },
    ticket: {
      // 🎟 Admission tickets
      symbol: <>&#127903;</>,
      label: "Ticket",
    },
    time: {
      // ⏰ Alarm clock
      symbol: <>&#9200;</>,
      label: "Time",
    },
    wonder: {
      // 🤔
      symbol: <>&#129300;</>,
      label: "Wonder",
    },
    workshop: {
      // 📝
      symbol: <>&#128221;</>,
      label: "Workshop",
    },
    someFacebook: {
      // 
      symbol: <>&#57368;</>,
      label: "Facebook",
    },
    someInstagram: {
      // 
      symbol: <>&#57369;</>,
      label: "Instagram",
    },
    someLinkedIn: {
      // 
      symbol: <>&#57376;</>,
      label: "LinkedIn",
    },
    someTwitter: {
      // 
      symbol: <>&#57377;</>,
      label: "Twitter",
    },
  };

  return (
    <span
      className={classNames(
        "font-sans-symbols",
        animated ? "motion-safe:animate-symbol-bounce" : "",
        animatedSlow ? "motion-safe:animate-symbol-bounce-slow" : "",
        animatedHover
          ? "hover:motion-safe:animate-symbol-bounce group-hover:motion-safe:animate-symbol-bounce"
          : "",
        className
      )}
    >
      <Emoji symbol={icons[name]?.symbol} label={icons[name]?.label} />
    </span>
  );
};

EmojiIcon.defaultProps = {
  name: "",
};

EmojiIcon.propTypes = {
  className: PropTypes.string,
  animated: PropTypes.bool,
  animatedSlow: PropTypes.bool,
  animatedHover: PropTypes.bool,
  /**
   * Name:  businessDev, coffee, clap, content, design, flagUk, flagNo, important, leadership,
   * livestream, party, presentation, productService, question,
   * smile, stage, sustainability, tech, ticket, time, wonder, workshop
   * someFacebook, someInstagram, someLinkedIn, someTwitter
   * */
  name: PropTypes.string,
};

export default EmojiIcon;
